import type {
  ConvoyTrackSegmentDraft,
  MembershipSnapshot,
} from './types';

function groupsEqual(
  a: Map<string, number[]>,
  b: Map<string, number[]>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [k, ids] of a) {
    const other = b.get(k);
    if (!other || other.length !== ids.length) return false;
    const sa = [...ids].sort((x, y) => x - y).join(',');
    const sb = [...other].sort((x, y) => x - y).join(',');
    if (sa !== sb) return false;
  }
  return true;
}

/**
 * Collapse consecutive snapshots with the same partition into segment drafts.
 * One draft per groupKey that exists in a stable partition window.
 */
export function collapseSnapshotsToSegmentDrafts(
  snapshots: readonly MembershipSnapshot[],
): ConvoyTrackSegmentDraft[] {
  if (snapshots.length === 0) return [];

  type Open = {
    groupKey: string;
    memberDeviceIds: number[];
    startSec: number;
    endSec: number;
  };

  const closed: ConvoyTrackSegmentDraft[] = [];
  let open: Open[] = [];
  let prevGroups: Map<string, number[]> | null = null;

  function flushOpen() {
    for (const o of open) {
      if (o.endSec < o.startSec) continue;
      closed.push({
        groupKey: o.groupKey,
        memberDeviceIds: o.memberDeviceIds,
        startSec: o.startSec,
        endSec: o.endSec,
      });
    }
    open = [];
  }

  function openFromGroups(groups: Map<string, number[]>, t: number) {
    open = [];
    for (const [groupKey, memberDeviceIds] of groups) {
      const ids = [...memberDeviceIds].sort((a, b) => a - b);
      open.push({
        groupKey,
        memberDeviceIds: ids,
        startSec: t,
        endSec: t,
      });
    }
  }

  for (const snap of snapshots) {
    if (prevGroups === null) {
      openFromGroups(snap.groups, snap.timestampSec);
      prevGroups = snap.groups;
      continue;
    }

    if (groupsEqual(prevGroups, snap.groups)) {
      for (const o of open) {
        o.endSec = snap.timestampSec;
      }
    } else {
      for (const o of open) {
        o.endSec = snap.timestampSec;
      }
      flushOpen();
      openFromGroups(snap.groups, snap.timestampSec);
      prevGroups = snap.groups;
    }
  }

  flushOpen();
  return closed;
}
